import React, { useRef, useEffect, useState } from 'react';
import { Chart as ChartJS, ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement } from 'chart.js';
import { Bar } from 'react-chartjs-2';
import axios from '../axios';

// Register necessary components
ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement);

const BarChart = ({auth}) => {
    const [noStudent, setNoStudents] = useState([]);
    const [month, setMonth] = useState([]);
    const [error, setError] = useState(null); // State for handling errors

    useEffect(() => {
        const fetchDatabyCentre = async () => {
            try {
                // Validate if centre_id is available
                if (!auth.centre) {
                    throw new Error("Centre ID is missing or invalid.");
                }

                const response = await axios.post(`/students/studentCountbyYearByCentreID`, {
                    centre_id: auth.centre
                });

                const data = response.data;
                setMonth(data.map(item => item.month));
                setNoStudents(data.map(item => item.total_students));
            } catch (error) {
                console.error('Error fetching data by centre:', error);
                setError(error.message); // Capture the error message
            }
        };

        const fetchChartData = async () => {
            try {
                const response = await axios.get('/students/StudentCountbyYear');
                const data = response.data;

                setMonth(data.map(item => item.month));
                setNoStudents(data.map(item => item.total_students));
            } catch (error) {
                console.error('Error fetching chart data:', error);
                setError(error.message); // Capture the error message
            }
        };

        // Conditionally call the appropriate data fetch function based on user type
        if (auth.userType === 'Admin') {
            fetchDatabyCentre();
        } else {
            fetchChartData();
        }
    }, [auth.userType, auth.centre]);

    const chartRef = useRef(null);

    useEffect(() => {
        const chart = chartRef.current?.chartInstance;

        return () => {
            if (chart) {
                chart.destroy();
            }
        };
    }, []);

    const data = {
        labels: month,
        datasets: [
            {
                label: 'Students',
                data: noStudent,
                backgroundColor: 'rgba(54, 162, 235, 0.7 )', // Blue color with transparency
                borderColor: 'rgba(54, 162, 235, 2)', // Solid blue border
                borderWidth: 1,
                barThickness: 25, // Adjust the width of the bars
            },
        ],
    };

    const options = {
        responsive: true,
        maintainAspectRatio: false, // Allow height customization
        plugins: {
            tooltip: {
                mode: 'index',
                intersect: false,
            },
            legend: {
                display: false,
            },
        },
        scales: {
            x: {
                grid: {
                    display: false, // Hide x-axis grid lines
                },
                ticks: {
                    display: true, // Show x-axis labels
                },
            },
            y: {
                grid: {
                    display: false, // Hide y-axis grid lines
                },
                ticks: {
                    display: true, // Show y-axis labels
                },
            },
        },
    };

    // Render fallback UI in case of an error
    if (error) {
        return (
            <div className="error-message">
                <p>Error loading chart data: {error}</p>
            </div>
        );
    }

    return (
        <div className="bar-chart-container">
            <Bar
                ref={chartRef}
                data={data}
                options={options}
            />
        </div>
    );
};

export default BarChart;
